import { useQuery } from '@apollo/client';
import { ArrowUpRight, Crown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { SUPER_HERO_ACTIVE_PROMOTION, SUPER_HERO_PROMOTION, type SuperHeroActivePromotionData, type SuperHeroPromotionData } from '@/queries/campaignEntry';
import { useAuth } from '@/contexts/AuthContext';
import { useCampaignEntryCopy } from '@/i18n/campaignEntry';
import { SUPER_HERO_BANNER_URL, SUPER_HERO_CAMPAIGN_ID, SUPER_HERO_CAMPAIGN_PATH, SUPER_HERO_LOGO_URL } from '@/lib/campaigns/superHeroCampaign';
import { CampaignArtwork } from './CampaignArtwork';
import './superHeroEntry.css';

interface SuperHeroSidebarBannerProps {
  collapsed: boolean;
  previewEligible: boolean;
  onNavigate?: () => void;
}

type PromotionCampaign = Omit<NonNullable<SuperHeroPromotionData['adminCpmCampaign']>, '__typename'>;
const queryOptions = {
  variables: { id: SUPER_HERO_CAMPAIGN_ID },
  fetchPolicy: 'no-cache' as const,
  errorPolicy: 'all' as const,
  context: { queryDeduplication: false },
  pollInterval: 60_000,
};

function isAvailable(campaign: PromotionCampaign | null | undefined, statuses: string[]): campaign is PromotionCampaign {
  const deadline = campaign?.end_date ? Date.parse(campaign.end_date) : null;
  return !!campaign && campaign.id === SUPER_HERO_CAMPAIGN_ID && campaign.campaign_type === 'ranking'
    && statuses.includes(campaign.status)
    && (deadline === null || (Number.isFinite(deadline) && deadline > Date.now()));
}

export function SuperHeroSidebarBanner({ collapsed, previewEligible, onNavigate }: SuperHeroSidebarBannerProps) {
  const { user, sessionVerified } = useAuth();
  if (!user || !sessionVerified) return null;

  // Only this observer remounts on account changes. A late authorized response
  // from the previous account must never become the current account's banner.
  return <ActiveSuperHeroSidebarBanner key={user.id} collapsed={collapsed} previewEligible={previewEligible} onNavigate={onNavigate} />;
}

function ActiveSuperHeroSidebarBanner({ collapsed, previewEligible, onNavigate }: SuperHeroSidebarBannerProps) {
  const { data, loading, error, errors } = useQuery<SuperHeroActivePromotionData>(SUPER_HERO_ACTIVE_PROMOTION, queryOptions);
  const campaign = data?.myCpmCampaign;
  if (!loading && !error && !errors?.length && isAvailable(campaign, ['active'])) {
    return <SuperHeroSidebarLink campaign={campaign} collapsed={collapsed} onNavigate={onNavigate} preview={false} />;
  }
  return previewEligible ? <PreviewSuperHeroSidebarBanner collapsed={collapsed} onNavigate={onNavigate} /> : null;
}

function PreviewSuperHeroSidebarBanner({ collapsed, onNavigate }: Pick<SuperHeroSidebarBannerProps, 'collapsed' | 'onNavigate'>) {
  const { data, loading, error, errors } = useQuery<SuperHeroPromotionData>(SUPER_HERO_PROMOTION, queryOptions);
  const campaign = data?.adminCpmCampaign;
  // Prelaunch visibility still requires this exact campaign's admin read.
  if (loading || error || errors?.length || !isAvailable(campaign, ['draft', 'scheduled'])) return null;
  return <SuperHeroSidebarLink campaign={campaign} collapsed={collapsed} onNavigate={onNavigate} preview />;
}

function SuperHeroSidebarLink({ campaign, collapsed, onNavigate, preview }: Pick<SuperHeroSidebarBannerProps, 'collapsed' | 'onNavigate'> & { campaign: PromotionCampaign; preview: boolean }) {
  const copy = useCampaignEntryCopy();
  const label = preview ? copy.sidebarPreviewLabel : copy.sidebarLabel;
  if (collapsed) {
    return <Tooltip>
      <TooltipTrigger asChild>
        <Link to={SUPER_HERO_CAMPAIGN_PATH} onClick={onNavigate} aria-label={label} className="super-hero-sidebar-compact">
          <CampaignArtwork key={campaign.logo_url || SUPER_HERO_LOGO_URL} src={campaign.logo_url || SUPER_HERO_LOGO_URL} alt={copy.logoAlt} compact />
          <span className="super-hero-sidebar-crown" aria-hidden="true"><Crown /></span>
        </Link>
      </TooltipTrigger>
      <TooltipContent side="right">{copy.sidebarName} · {copy.sidebarBadge}</TooltipContent>
    </Tooltip>;
  }

  return <Link to={SUPER_HERO_CAMPAIGN_PATH} onClick={onNavigate} aria-label={label} className="super-hero-sidebar-banner">
    <CampaignArtwork key={campaign.banner_url || SUPER_HERO_BANNER_URL} src={campaign.banner_url || SUPER_HERO_BANNER_URL} alt={copy.artworkAlt} className="super-hero-sidebar-art" />
    <div className="super-hero-sidebar-copy">
      <span className="super-hero-sidebar-preview">{copy.sidebarBadge}</span>
      <span className="super-hero-sidebar-name">{copy.sidebarName}</span>
      <span className="super-hero-sidebar-prize">{copy.prize} <span>{copy.prizeLabel}</span></span>
    </div>
    <span className="super-hero-sidebar-action">{copy.sidebarCta}<ArrowUpRight aria-hidden="true" /></span>
  </Link>;
}
